/**
 * Product analytics — a thin, named wrapper over PostHog.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * PostHog has been initialised since launch (src/index.js) and captured exactly
 * ONE event: `user_logged_in`. So none of the questions that matter about pricing
 * were answerable — how many communities reach the free cap, how many people
 * click upgrade, where checkout is abandoned.
 *
 * These events ship BEFORE the pricing change deliberately. Instrumenting after
 * launch would leave the first and most informative cohort — the one that tells
 * us whether a 5-member free tier is right — unmeasurable, and we'd end up
 * re-deciding pricing on anecdote.
 *
 * Event names live here as constants rather than as string literals at call
 * sites. This codebase has repeatedly been bitten by the same class of bug:
 * a name written in one place and typed slightly differently in another
 * (`itemType: 'request'` vs `'prayer_request'` silently skipped a whole
 * moderation branch; HIGH_RISK_REASONS drifted from the keys the UI emitted).
 * A funnel event that silently never fires is the same failure and is even
 * harder to notice, because nothing breaks — the dashboard is just quietly wrong.
 */
import posthog from 'posthog-js';
import { openedPage, takePushOpenMark } from '@/lib/notificationTarget';

export const EVENTS = Object.freeze({
  // ── Community lifecycle
  COMMUNITY_CREATED: 'community_created',

  // ── Growth / capacity funnel
  MEMBER_INVITED: 'member_invited',
  MEMBER_JOINED: 'member_joined',
  CAPACITY_WARNING_SHOWN: 'capacity_warning_shown',
  CAPACITY_BLOCKED_INVITE: 'capacity_blocked_invite',

  // ── Upgrade funnel
  UPGRADE_PROMPT_SHOWN: 'upgrade_prompt_shown',
  UPGRADE_CLICKED: 'upgrade_clicked',
  PLAN_SELECTED: 'plan_selected',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  CHECKOUT_ABANDONED: 'checkout_abandoned',

  // ── Billing delegation (Iter 118.25)
  // The volunteer-leader-can't-pay path. Worth measuring separately from the
  // normal funnel: if delegated checkouts convert far better than direct ones,
  // the paywall is landing on the wrong person and that is the finding.
  BILLING_DELEGATION_STARTED: 'billing_delegation_started',
  BILLING_DELEGATION_SENT: 'billing_delegation_sent',
  BILLING_DELEGATED_CHECKOUT_OPENED: 'billing_delegated_checkout_opened',

  // ── Getting the store apps (P0211): a tap on a store badge or the Android bar's Get, with which store and where.
  APP_STORE_LINK_CLICKED: 'app_store_link_clicked',

  // ── Taking part (P0215, Dennis 2026-10-17: are people praying, sharing, joining, and following their notifications?)
  // Praying, for someone else or for one's own request: where, and whether words went with it (a verse, a note or a
  // prayer). Never the request or what was written.
  PRAYED: 'prayed',
  // A prayer request shared: where from, and with how many groups (0 = kept in the journal). Never its words.
  PRAYER_REQUEST_SHARED: 'prayer_request_shared',
  // A notification opened: from the bell or by tapping a push (phones, and the website's own push), with the page it
  // opens; from the bell also its type. Never the address itself.
  NOTIFICATION_OPENED: 'notification_opened',

  // Trial events are added with the surfaces that fire them, not ahead of them.
  // A declared-but-never-fired event is worse than an absent one: it makes the
  // funnel look like it has coverage it does not have.
  // test_iteration118_22_analytics_events enforces this — every constant here
  // must have a real call site.
});

/**
 * Capture an event. Never throws — analytics must not be able to break a user
 * flow, and several of these fire inside checkout and invite paths where an
 * exception would be very visible.
 */
export const track = (event, properties = {}) => {
  try {
    if (!event) return;
    posthog?.capture?.(event, properties);
  } catch (_) {
    /* analytics is best-effort by design */
  }
};

/**
 * Fire once per mount for "was this surface seen" events. Impression events are
 * the ones most likely to be double-counted, because the components that show
 * them (modals, banners) re-render on unrelated state changes — and a doubled
 * denominator makes a conversion rate look half as good as it is.
 *
 * Usage:
 *   const seen = useRef(false);
 *   useEffect(() => { if (open) trackOnce(seen, EVENTS.X, {...}); }, [open]);
 */
export const trackOnce = (ref, event, properties = {}) => {
  if (!ref || ref.current) return;
  ref.current = true;
  track(event, properties);
};

/**
 * A tap on one of the website's push notifications opens its page with `opened=push` on the address (public/sw.js). Called
 * once at start-up (src/index.js), before the app reads the address: it counts the open and takes the mark off again.
 */
export const trackWebPushOpen = (win = typeof window !== 'undefined' ? window : undefined) => {
  try {
    const { opened, href } = takePushOpenMark(win.location.href);
    if (!opened) return false;
    win.history.replaceState(win.history.state, '', href);
    track(EVENTS.NOTIFICATION_OPENED, { via: 'push', page: openedPage(href) });
    return true;
  } catch (_) {
    return false;
  }
};
